import { useEffect } from 'react';
import { useBuyerMachine } from '../../BuyerMachineContext';
import { ApplicationMachineActorRef } from '../ApplicationMachineContext';

type BuyerState = ReturnType<typeof useBuyerMachine>['state'];

/**
 * Emits events to the application machine based on changes to the buyer machine state.
 * @param applicationService
 * @param buyerState
 */
export const useBuyerMachineEventEmitter = (
  applicationService: ApplicationMachineActorRef,
  buyerState: BuyerState
): void => {
  useEffect(() => {
    const appState = applicationService.getSnapshot();

    const sendCreateApplication = (): void => {
      applicationService.send({
        type: 'SEND_CREATE_APPLICATION',
        params: { context: applicationService.getSnapshot().context },
      });
    };
    const sendFetchLatestApplication = (): void => {
      applicationService.send({ type: 'SEND_FETCH_LATEST_APPLICATION' });
    };

    const isInitial = buyerState.matches({ ready: { complete: 'initial' } });
    const isContactUpdated = buyerState.matches({
      ready: { complete: 'contactUpdated' },
    });
    const isContactUpdatedNoChange = buyerState.matches({
      ready: { complete: 'contactUpdatedWithoutChange' },
    });
    const isDobUpdated = buyerState.matches({
      ready: { complete: 'dobUpdated' },
    });
    const isDobUpdatedNoChange = buyerState.matches({
      ready: { complete: 'dobUpdatedWithoutChange' },
    });
    const isEmailUpdated = buyerState.matches({
      ready: { complete: 'emailUpdated' },
    });
    const isCompleteIdentity = buyerState.matches({
      ready: 'completeIdentity',
    });

    // Invalid application: only re-create when the buyer has (re-)entered DOB.
    if (appState.matches({ ready: 'invalid' })) {
      if (isDobUpdated || isDobUpdatedNoChange) {
        sendCreateApplication();
      }
      return;
    }

    // Newly-complete buyer: fetch the latest application unless one is already approved.
    if (isInitial && !appState.matches({ ready: 'approved' })) {
      sendFetchLatestApplication();
      return;
    }

    // Contact / DOB / email actually changed -> create a new application.
    if (isContactUpdated || isDobUpdated || isEmailUpdated) {
      sendCreateApplication();
      return;
    }

    // Contact / DOB re-confirmed without change, or RBC identity buyer -> fetch latest.
    if (
      isContactUpdatedNoChange ||
      isDobUpdatedNoChange ||
      isCompleteIdentity
    ) {
      sendFetchLatestApplication();
    }
  }, [buyerState]);
};
